/**
 * Frame orchestration. Draw calls per frame: sensor mask (1) → territory (1) → coast (1)
 * → vector shapes + segments (2) → bloom (6) → CRT composite (1); labels on a 2D canvas.
 */
import type { LoadedWorld } from '@sa/mapdata';
import { FRONT_STATUSES, type HexIdx } from '@sa/sim';
import type { Camera } from '../input/camera';
import type { GameStore } from '../state/store';
import { COLORS } from '../theme';
import { CoastLayer } from './coast';
import { CrtPost } from './crt';
import type { FrameParams } from './frame';
import type { HexLookup } from './hexfield';
import { buildLandMask } from './landmask';
import { LabelLayer } from './labels';
import type { Palette } from './palette';
import { SceneBuilder, type Overlay, type SceneOut } from './scene';
import { TerritoryLayer } from './territory';
import { VectorBatch } from './vector';
import { VisLayer } from './vis';

const MAX_GL_DPR = 1.5;
/** the top HUD blocks (css px): credits at the left, share bar + clock at the right; map glyphs there are not drawn */
const HUD_BLOCK_H = 74;
const HUD_CREDITS_W = 300;
const HUD_SHARE_W = 420;

/** Dev-only stage timer: forces GPU sync after each stage, logs averages every 60 frames. */
/** a betrayal's red pulse on the ex-partner's land lasts this long (ms): an alliance's, a ceasefire's */
const BETRAYAL_PULSE_MS = 4_500;
const CEASEFIRE_PULSE_MS = 2_500;
/** …and an alliance's betrayal glitches the screen this long (ms) */
const BETRAYAL_GLITCH_MS = 650;

/**
 * Whose land lights up: a nation that just betrayed us (red, beating fast, fading out) before the
 * Tab board's pointer (its own colour: fades in over 150 ms, then breathes every 1.2 s).
 */
function hilite(store: GameStore, nowMs: number): { hilite: number; hiliteK: number; hiliteRed: number } {
  const b = store.betrayalPulse;
  const age = b ? nowMs - b.atMs : -1;
  const dur = b?.small ? CEASEFIRE_PULSE_MS : BETRAYAL_PULSE_MS;
  if (b && age >= 0 && age < dur) {
    const k = Math.min(1, age / 120) * Math.sqrt(1 - age / dur) * (0.55 + 0.45 * Math.cos((age / 450) * 2 * Math.PI));
    return { hilite: b.player, hiliteK: 1.3 * k, hiliteRed: 1 };
  }
  const h = store.highlight;
  if (!h) return { hilite: 0, hiliteK: 0, hiliteRed: 0 };
  const t = Math.max(0, nowMs - h.atMs);
  return { hilite: h.player, hiliteK: Math.min(1, t / 150) * (0.7 + 0.3 * Math.cos((t / 1200) * 2 * Math.PI)), hiliteRed: 0 };
}

/** The screen's glitch right after an ally betrayed us, 1 → 0 (0: none). */
function glitch(store: GameStore, nowMs: number): number {
  const b = store.betrayalPulse;
  const age = b && !b.small ? nowMs - b.atMs : -1;
  return age >= 0 && age < BETRAYAL_GLITCH_MS ? 1 - age / BETRAYAL_GLITCH_MS : 0;
}

export class StageProfiler {
  private t = 0;
  private sums = new Map<string, number>();
  private frames = 0;

  start(): void {
    this.t = performance.now();
  }

  mark(name: string, gl?: WebGL2RenderingContext): void {
    gl?.finish();
    const now = performance.now();
    this.sums.set(name, (this.sums.get(name) ?? 0) + now - this.t);
    this.t = now;
  }

  end(): void {
    if (++this.frames < 60) return;
    console.warn(`[prof] ${[...this.sums].map(([k, v]) => `${k} ${(v / this.frames).toFixed(1)}ms`).join(' · ')}`);
    this.sums.clear();
    this.frames = 0;
  }
}

export class Renderer {
  private gl: WebGL2RenderingContext;
  private territory: TerritoryLayer;
  private coast: CoastLayer;
  private vis: VisLayer;
  private vb: VectorBatch;
  private crt: CrtPost;
  private scene: SceneBuilder;
  private labels: LabelLayer;
  /** dev: per-stage timings with gl.finish() (`?prof=1`) */
  prof: StageProfiler | null = null;
  /** dev: stages to leave out (`?skip=`) */
  skip: ReadonlySet<string> = new Set();
  private dpr = 1;
  private scale = 1;
  private css = { w: 1, h: 1, dpr: 1 };
  private w = 1;
  private h = 1;

  constructor(
    private canvas: HTMLCanvasElement,
    labelCanvas: HTMLCanvasElement,
    world: LoadedWorld,
    names: ReadonlyMap<HexIdx, string>,
    lookup: HexLookup,
  ) {
    const gl = canvas.getContext('webgl2', {
      antialias: false,
      alpha: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',
      preserveDrawingBuffer: false,
    });
    if (!gl) throw new Error('WebGL2 is not available');
    this.gl = gl;
    this.territory = new TerritoryLayer(gl, world, buildLandMask(world), lookup);
    this.coast = new CoastLayer(gl, world, this.territory);
    this.vis = new VisLayer(gl);
    this.vb = new VectorBatch(gl);
    this.crt = new CrtPost(gl);
    this.scene = new SceneBuilder(this.vb, this.vis);
    this.labels = new LabelLayer(labelCanvas, names);
  }

  /** True when WebGL runs on a CPU rasterizer (e.g. Chrome's fallback on blocklisted GPUs). */
  get software(): boolean {
    const gl = this.gl;
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    const name = String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
    return /swiftshader|llvmpipe|software|softpipe/i.test(name);
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    this.css = { w: cssW, h: cssH, dpr };
    // the CRT look is soft anyway: cap the GL buffer density, keep text at full density
    this.dpr = Math.min(dpr, MAX_GL_DPR) * this.scale;
    this.w = Math.max(1, Math.round(cssW * this.dpr));
    this.h = Math.max(1, Math.round(cssH * this.dpr));
    this.canvas.width = this.w;
    this.canvas.height = this.h;
    this.labels.resize(cssW, cssH, dpr);
  }

  /** GL resolution scale (dynamic resolution); 1 = full. */
  get resolutionScale(): number {
    return this.scale;
  }

  set resolutionScale(s: number) {
    if (Math.abs(s - this.scale) < 0.01) return;
    this.scale = s;
    this.resize(this.css.w, this.css.h, this.css.dpr);
  }

  render(store: GameStore, pal: Palette, cam: Camera, tick: number, nowMs: number, ov: Overlay): SceneOut {
    const gl = this.gl;
    if (store.hexDirty) {
      this.territory.uploadState(store.hexState);
      store.hexDirty = false;
    }
    if (store.flipDirty) {
      this.territory.uploadFlip(store.flipTime);
      store.flipDirty = false;
    }
    const time = nowMs / 1000;
    // dev: scripted screenshot flows (apps/client/scripts) read world ↔ screen positions
    if (import.meta.env.DEV) (window as unknown as { __saDebug: unknown }).__saDebug = { store, cam };
    const prof = this.prof;
    prof?.start();
    const view = cam.visibleRect(60);
    const w = cam.vp.w;
    const underHud = (x: number, y: number) => {
      const [sx, sy] = cam.worldToScreen(x, y);
      return sy < HUD_BLOCK_H && (sx < HUD_CREDITS_W || sx > w - HUD_SHARE_W);
    };
    const out = this.scene.build(store, pal, tick, nowMs, time, ov, cam.zoom, view, underHud);
    prof?.mark('scene');
    const f: FrameParams = {
      viewW: this.w,
      viewH: this.h,
      dpr: this.dpr,
      camX: cam.cx,
      camY: cam.cy,
      zoomPx: cam.zoom * this.dpr,
      time,
      me: store.view.me,
      allyMask: pal.allyMask,
      ownerColors: pal.ownerColors,
      front: FRONT_STATUSES.indexOf(store.view.front.status),
      ...hilite(store, nowMs),
    };
    const skip = this.skip;
    this.vis.render(f);
    prof?.mark('vis', gl);
    this.crt.begin(this.w, this.h);
    gl.clearColor(COLORS.bg[0], COLORS.bg[1], COLORS.bg[2], 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (!skip.has('territory')) this.territory.draw(f, this.vis.texture);
    prof?.mark('territory', gl);
    if (!skip.has('coast')) this.coast.draw(f, this.vis.texture, view, cam.zoom);
    prof?.mark('coast', gl);
    if (!skip.has('vector')) this.vb.draw(f);
    prof?.mark('vector', gl);
    this.crt.finish(this.w, this.h, this.dpr, time, out.flash, skip.has('bloom'), glitch(store, nowMs));
    prof?.mark('crt', gl);
    if (!skip.has('labels')) this.labels.draw(cam, store, pal, out);
    prof?.mark('labels');
    prof?.end();
    return out;
  }
}
